import React, { useState, useEffect, useRef, useContext } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import api from '../../services/api'
import { AuthContext } from '../../context/Auth/AuthContext'

const useStyles = makeStyles(() => ({
  stickerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
    maxWidth: '150px',
    maxHeight: '150px',
    backgroundColor: 'transparent',
  },
  sticker: {
    width: 'auto',
    height: 'auto',
    maxWidth: '150px',
    maxHeight: '150px',
    objectFit: 'contain',
    borderRadius: '0px',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'block',
  },
  stickerLoading: {
    width: '100px',
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
}))

const StickerPreview = ({ stickerUrl, onClick }) => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const [fetching, setFetching] = useState(true)
  const [blobUrl, setBlobUrl] = useState('')
  const [error, setError] = useState(false)
  const blobUrlRef = useRef(null)

  useEffect(() => {
    if (!stickerUrl) return undefined

    let cancelled = false

    if (blobUrlRef.current) {
      window.URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }

    const fetchSticker = async () => {
      try {
        setFetching(true)
        setError(false)

        let urlToFetch = stickerUrl
        const baseURL = process.env.REACT_APP_BACKEND_URL || api.defaults.baseURL || ''
        const companyId = user?.companyId || ''

        if (!companyId) {
          throw new Error('companyId nao disponivel')
        }

        if (urlToFetch.startsWith('http')) {
          // full URL, keep as-is
        } else if (urlToFetch.startsWith('/public/')) {
          urlToFetch = baseURL ? `${baseURL}${urlToFetch}` : urlToFetch
        } else if (urlToFetch.startsWith('stickers/')) {
          urlToFetch = `${baseURL}/public/company${companyId}/${urlToFetch}`
        } else {
          const fileName = urlToFetch
          const urlsToTry = [
            `${baseURL}/public/company${companyId}/stickers/${fileName}`,
            `${baseURL}/public/company${companyId}/stickers/salvos/${fileName}`,
            `${baseURL}/public/company${companyId}/${fileName}`,
          ]

          let foundUrl = null
          for (const url of urlsToTry) {
            try {
              const testResponse = await fetch(url, { method: 'HEAD' })
              if (testResponse.ok) {
                foundUrl = url
                break
              }
            } catch (_error) {
              // try next candidate
            }
          }

          urlToFetch = foundUrl || urlsToTry[0]
        }

        let response
        try {
          response = await fetch(urlToFetch)
        } catch (fetchError) {
          const fileName = urlToFetch.split('/').pop() || stickerUrl.split('/').pop() || stickerUrl
          const fallbackUrls = [
            `${baseURL}/public/company${companyId}/stickers/${fileName}`,
            `${baseURL}/public/company${companyId}/stickers/salvos/${fileName}`,
            `${baseURL}/public/company${companyId}/${fileName}`,
          ]

          let foundFallback = false
          for (const fallbackUrl of fallbackUrls) {
            if (fallbackUrl === urlToFetch) {
              continue
            }

            try {
              response = await fetch(fallbackUrl)
              if (response.ok) {
                urlToFetch = fallbackUrl
                foundFallback = true
                break
              }
            } catch (_error) {
              // try next fallback
            }
          }

          if (!foundFallback) {
            throw fetchError
          }
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - URL: ${urlToFetch}`)
        }

        const blob = await response.blob()

        if (cancelled) return

        if (blob.size === 0) {
          throw new Error('Arquivo vazio ou corrompido')
        }

        const url = window.URL.createObjectURL(blob)
        blobUrlRef.current = url
        setBlobUrl(url)
        setFetching(false)
      } catch (err) {
        if (cancelled) return
        setError(true)
        setFetching(false)
      }
    }

    fetchSticker()

    return () => {
      cancelled = true
      if (blobUrlRef.current) {
        window.URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [stickerUrl, user?.companyId])

  const handleImageClick = event => {
    const img = event.target
    const modal = document.createElement('div')
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      cursor: pointer;
    `

    const modalImg = document.createElement('img')
    modalImg.src = img.src
    modalImg.style.cssText = `
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
    `

    modal.appendChild(modalImg)
    document.body.appendChild(modal)

    const closeModal = () => {
      document.body.removeChild(modal)
    }

    modal.addEventListener('click', closeModal)
  }

  if (error) {
    return (
      <div className={classes.stickerContainer}>
        <span style={{ color: '#999', fontSize: '12px' }}>Erro ao carregar sticker</span>
      </div>
    )
  }

  if (fetching) {
    return (
      <div className={classes.stickerContainer}>
        <div className={classes.stickerLoading}>
          <span style={{ color: '#999', fontSize: '12px' }}>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.stickerContainer} onClick={onClick}>
      {blobUrl && (
        <img
          className={classes.sticker}
          src={blobUrl}
          alt='Sticker'
          onClick={event => {
            if (onClick) {
              event.stopPropagation()
              onClick(event)
            } else {
              handleImageClick(event)
            }
          }}
          onError={() => {
            setError(true)
          }}
          style={{
            cursor: 'pointer',
            imageRendering: 'auto',
          }}
        />
      )}
    </div>
  )
}

export default StickerPreview
